import BaseService from './BaseService';
import {
  ArchivingConversationMetadataResponse,
  ArchivingConversationResponse,
  ArchivingExportDownloadResponse,
  ArchivingExportListResponse,
  ArchivingExportResponse,
  ArchivingFilterNode,
  ArchivingMessageSearchResponse,
} from '../apis/ArchivingAPI';

export default class ArchivingService extends BaseService {
  /**
   * Searches archived messages for an organization via the analytics-service message search endpoint.
   */
  async searchMessages(
    organizationId: string,
    query: string,
    options: {
      startDate?: string;
      endDate?: string;
      hasAttachment?: boolean;
      senderAccountIds?: string[];
      recipientAccountIds?: string[];
      filter?: ArchivingFilterNode;
      limit?: number;
    } = {}
  ): Promise<ArchivingMessageSearchResponse> {
    return this.host.api.archiving.searchMessages(organizationId, {
      orgId: organizationId,
      query,
      ...options,
    });
  }

  /**
   * Fetches every message in one conversation via the analytics-service conversation endpoint —
   * used by the "View Thread" action on a search result, keyed off that result's conversationId.
   */
  async getConversation(
    organizationId: string,
    conversationId: string,
    options: { limit?: number } = {}
  ): Promise<ArchivingConversationResponse> {
    return this.host.api.archiving.getConversation(organizationId, {
      orgId: organizationId,
      conversationId,
      ...options,
    });
  }

  /**
   * Fetches a conversation's load-once details (type, true message count) via the
   * analytics-service conversation metadata endpoint — separate from getConversation's message
   * list so it stays correct once that list paginates. See design doc 0002 §5.12.
   */
  async getConversationMetadata(
    organizationId: string,
    conversationId: string
  ): Promise<ArchivingConversationMetadataResponse> {
    return this.host.api.archiving.getConversationMetadata(organizationId, {
      orgId: organizationId,
      conversationId,
    });
  }

  /**
   * Queues an export for a list of message ids via the analytics-service export endpoint. The
   * caller resolves the export's source (selected messages, a full conversation thread, a saved
   * search) into messageIds beforehand.
   */
  async startExport(
    organizationId: string,
    options: {
      exportName?: string;
      messageIds: string[];
      includeAttachments?: boolean;
      notes?: string;
    }
  ): Promise<ArchivingExportResponse> {
    return this.host.api.archiving.startExport(organizationId, {
      orgId: organizationId,
      ...options,
    });
  }

  /**
   * Lists the caller's own export jobs, most recent first — powers the Export History tab. Never
   * every export in the org; there's no archiving-admin role yet to gate that view.
   */
  async listExports(organizationId: string): Promise<ArchivingExportListResponse> {
    return this.host.api.archiving.listExports(organizationId, {
      orgId: organizationId,
    });
  }

  /**
   * Gets a short-lived presigned download URL for one of the caller's own ready export jobs.
   * Throws (via the underlying HTTP call) if the job isn't found, isn't ready yet, or has expired.
   */
  async getExportDownloadUrl(
    organizationId: string,
    jobId: string
  ): Promise<ArchivingExportDownloadResponse> {
    return this.host.api.archiving.getExportDownloadUrl(organizationId, {
      orgId: organizationId,
      jobId,
    });
  }
}
